<script setup lang="ts">
import type { JournalPublisherDTO } from '~/shared/api/client/journals/journals.dto';
import { getJournalEditionUrl } from '~/shared/lib/journal-routes.const';
import { useJournalEditions } from '../lib/useJournalData';
import JournalCover from './JournalCover.vue';

/**
 * The head of the "Реклама" tab: the intro, then one list of editions drawn
 * twice — a row of names and a row of covers. Both rows come from the
 * journal_advertising publisher, so an editor changes the media kit without a
 * release (ADR 0001, decision 1)
 */
const props = defineProps<{
  block: JournalPublisherDTO;
}>();

// An edition link carries its tab prefix, and the tab is known from the reference
const { editions: reference } = await useJournalEditions();

const editions = computed(() =>
  props.block.publisher_news.flatMap((card) =>
    card.edition
      ? [
          {
            id: card.id,
            // The card may introduce an edition under a longer name than the
            // one the chips of the other tabs carry — "CRE (Commercial Real
            // Estate)" against plain "CRE"
            label: card.title || card.edition.name,
            to: getJournalEditionUrl(reference.value, card.edition),
            cover: card.image_one?.file_url,
          },
        ]
      : [],
  ),
);
</script>

<template>
  <section v-if="editions.length" :class="$style.root">
    <!--
      Fixed by the design, like the heading of the recommendations block: it
      introduces the publishing house, not this particular set of editions, and
      the layout never varies it. Drawing it from the block's title_block made
      a constant depend on a field nobody fills
    -->
    <p :class="$style.intro">
      Издательский Дом CRE — ведущая компания в России на рынке СМИ о
      коммерческой недвижимости. Наши издания:
    </p>
    <!--
      Filled the way a chip of the other tabs looks when it is the current one,
      but never a filter: these are addresses of the edition pages, and none of
      them is the page being read
    -->
    <div :class="$style.names">
      <NuxtLink
        v-for="edition in editions"
        :key="edition.id"
        :to="edition.to"
        :class="$style.name"
      >
        {{ edition.label }}
      </NuxtLink>
    </div>
    <div :class="$style.covers">
      <NuxtLink
        v-for="edition in editions"
        :key="edition.id"
        :to="edition.to"
        :class="$style.cover"
      >
        <JournalCover :title="edition.label" :src="edition.cover" />
      </NuxtLink>
    </div>
  </section>
</template>

<style module lang="scss">
.root {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 var(--space-2-5x) var(--space-5x);
}

// 32/600 at 110% in Black/2, three lines across 842 — wider and a shade
// lighter than the contacts heading under the covers
.intro {
  @include text_h4;
  color: var(--color-black-2);
  max-width: 842px;
  margin: 0 0 var(--space-3x);
  text-align: center;
}

.names {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-1x);
}

// A pill, not the square chip of the filter rows: 14/400 in a fully rounded
// black plate, dropping to 11 on a phone
.name {
  @include text_body2;
  display: flex;
  align-items: center;
  height: 24px;
  padding: 0 var(--space-2x);
  border-radius: 999px;
  color: var(--color-white);
  background-color: var(--color-black-1);
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--color-black-2);
  }
}

.covers {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: center;
  gap: var(--space-4x);
  margin-top: var(--space-5x);
}

// Six of them fill about three quarters of the content column in the layout,
// so the 3D edge of the full-size cover is thinned to match
.cover {
  --thickness: 8px;

  display: block;
  width: 104px;
}

/*
 * The layout staggers the row: every second cover drops half a step, so the six
 * of them read as a shelf and not as a line. Only while they stand on one line
 * — the row wraps on its own below this width, and there the offset would fall
 * between two rows instead of inside one
 */
@media (min-width: 1380px) {
  // Room under the row for the half-step the even covers take
  .covers {
    padding-bottom: var(--space-6x);
  }

  .cover:nth-child(even) {
    transform: translateY(var(--space-4x));
  }
}

@media (max-width: 599px) {
  .root {
    padding: 0 var(--space-2x) var(--space-4x);
  }

  // 24/600 at 110%, the same block one step down
  .intro {
    @include text_h5;
  }

  .name {
    @include text_caption2;
    height: 20px;
    padding: 0 var(--space-1-5x);
  }

  /*
   * Four in a row and two centred under them, as the mobile layout draws it.
   * The box is cut to exactly four covers so the row breaks there on every
   * phone — left to the screen, five would fit on the wider ones — and the
   * remainder stays centred, which a fixed grid of columns would not do
   */
  .covers {
    max-width: calc(4 * 56px + 3 * var(--space-2x));
    gap: var(--space-3x) var(--space-2x);
    margin-top: var(--space-3x);
  }

  .cover {
    width: 56px;
  }
}
</style>
